<!-- https://www.figma.com/file/reAk2sU4YpRajpQu2YBOHk/007%2F1.-%D0%AD%D0%BB%D0%B5%D0%BC%D0%B5%D0%BD%D1%82%D1%8B?node-id=3%3A19&mode=dev -->
<template>
  <div class="articles-carousel-wrapper">
    <div
      v-if="attributes.title"
      class="articles-carousel__title"
    >
      {{ attributes.title }}
    </div>

    <div
      ref="articlesCarousel"
      class="articles-carousel swiper"
      @mousemove="handleMouseMove"
      @mouseleave="handleMouseLeave"
    >
      <div class="swiper-wrapper">
        <div
          v-for="(article, index) in attributes.items"
          :key="`article-carousel-${elem.id}-${index}`"
          class="carousel-slide swiper-slide"
        >
          <div
            class="carousel-card"
            :class="getCardClasses(article)"
          >
            <BasePicture
              v-if="article.image && getShowImage(article)"
              class="slider-item__cover"
              :used-sized="[160, 187]"
              :sizes-by-device="{ mobile: '187px', tablet: '187px' }"
              :alt="article.image.alt"
              :sizes="article.image.sizes"
              :origin="article.image.sizes.at(-1)?.url || ''"
            />

            <TheLink
              class="carousel-card__link"
              :link="article.url"
            />
            <div class="carousel-card__gradient" />

            <div
              class="carousel-card__title"
              v-html="article.title"
            />
          </div>
        </div>
      </div>

      <button
        ref="swiperPrevButton"
        class="swiper-button-prev"
        :class="prevButtonClasses"
      />

      <button
        ref="swiperNextButton"
        class="swiper-button-next"
        :class="nextButtonClasses"
      />
    </div>
  </div>
</template>

<script lang="ts">
  import type { Swiper } from 'swiper';
  import type { PropType } from 'vue';
  import BasePicture from '@devhacker/shared/features/BasePicture/BasePicture.vue';
  import TheLink from '~/components/TheLink.vue';
  import type {
    ArticleCarouselSlide,
    ArticlesCarouselAttributes,
  } from '~/types/components/ArticleCarousel';
  import 'swiper/css';

  export default defineNuxtComponent({
    name: 'ArticlesCarousel',

    components: {
      TheLink,
      BasePicture,
    },

    props: {
      elem: {
        type: Object as PropType<TContent<ArticlesCarouselAttributes>>,
        required: true,
      },
    },

    data: () => ({
      swiper: null as null | Swiper,
      showNextButton: false,
      showPrevButton: false,
    }),

    computed: {
      attributes(): ArticlesCarouselAttributes {
        return this.elem.attributes;
      },

      prevButtonClasses(): Record<string, boolean> {
        return {
          visible: this.showPrevButton,
        };
      },

      nextButtonClasses(): Record<string, boolean> {
        return {
          visible: this.showNextButton,
        };
      },
    },

    async mounted() {
      const { default: Swiper } = await import('swiper');
      const { Navigation, Mousewheel } = await import('swiper/modules');
      const articlesCarouselWrapp = this.$refs.articlesCarousel as HTMLElement;

      this.swiper = new Swiper(articlesCarouselWrapp, {
        modules: [Navigation, Mousewheel],
        loop: false,
        cssMode: true,
        mousewheel: {
          forceToAxis: true,
        },

        slidesPerView: 'auto',
        navigation: {
          prevEl: this.$refs.swiperPrevButton as HTMLElement,
          nextEl: this.$refs.swiperNextButton as HTMLElement,
          disabledClass: 'swiper-button-hidden',
        },
      });
    },

    methods: {
      handleMouseLeave(): void {
        this.showNextButton = this.showPrevButton = false;
      },

      handleMouseMove({ currentTarget, clientX }: MouseEvent): void {
        if (!currentTarget) {
          return;
        }

        const { left, right } = (currentTarget as HTMLElement)?.getBoundingClientRect();
        const middle = (right - left) / 2;
        const mouseX = clientX - left;

        this.showPrevButton = mouseX < middle && !this.swiper?.isBeginning;
        this.showNextButton = mouseX > middle && !this.swiper?.isEnd;
      },

      getCardClasses({ image_url }: ArticleCarouselSlide): Record<string, boolean> {
        return {
          'carousel-card--empty': !image_url,
        };
      },

      getShowImage(article: ArticleCarouselSlide): boolean {
        return !!article.image?.sizes;
      },
    },
  });
</script>

<style lang="scss" scoped>
  //@use 'swiper/swiper.css' as *;

  $backgroundGradient: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0) 0%,
    rgba(0, 0, 0, 0.07) 25.52%,
    rgba(0, 0, 0, 0.52) 62.5%,
    rgba(0, 0, 0, 0.9) 94.35%
  );
  $noImageBackgroundSize: 130px;

  .articles-carousel-wrapper {
    margin: 8px 0px;

    @include tablet {
      margin: 16px 0px;
    }
  }

  .carousel-slide {
    width: 160px;
    height: 240px;
    padding-left: 12px;
    box-sizing: content-box;

    &:last-child {
      padding-right: 12px;
    }
  }

  .swiper-button-prev,
  .swiper-button-next,
  .swiper-button-hidden {
    width: 32px;
    height: 32px;

    border-radius: 32px;
    background: #fff;
    border: none;
    box-shadow: 0px 3px 10px 0px rgba(0, 0, 0, 0.25);

    opacity: 0;
    transition: 0.5s;

    &:after {
      content: '';
      width: 100%;
      height: 100%;
      background-image: url('data:image/svg+xml,%3Csvg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"%3E%3Cg id="direction/right/24px"%3E%3Cpath id="Rectangle 438" d="M13.8999 6.19238L7.99609 12.0962L13.8999 18" stroke="%23969698" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/%3E%3C/g%3E%3C/svg%3E%0A');
      background-size: contain;
    }
  }

  .swiper-button-prev {
    left: -16px;

    &.visible {
      left: 8px;
      opacity: 1;
    }
  }

  .swiper-button-next {
    right: -16px;

    &:after {
      scale: -1;
    }

    &.visible {
      right: 8px;
      opacity: 1;
    }
  }

  .articles-carousel {
    margin: 8px 0;

    @include tablet {
      margin: 16px 0;
    }
  }

  .articles-carousel__title {
    @include H2HeadlineText;

    display: inline-block;
    font-family: $secondFont;
    font-weight: 600;
    height: 32px;
    margin-bottom: 8px;

    @include tablet {
      @include H1HeadlineTextMobile;
      margin-bottom: 0px;
    }
  }

  .carousel-card {
    width: 100%;
    height: 100%;
    border-radius: 4px;
    position: relative;
    overflow: hidden;

    :deep() {
      img {
        transition: all 0.3s ease-in-out;
        transform: translateZ(0);
      }
    }

    @include hover {
      :deep() {
        img {
          transform: scale(1.1);
        }
      }

      .carousel-card__link {
        box-shadow: inset 0 0 0 2px var(--main-color);
      }

      .carousel-card__title {
        text-decoration-color: #fff;
      }
    }

    &--empty {
      transition: all 0.3s ease-in-out;
      background-color: var(--main-color);
      background-image: $backgroundGradient, url('../images/no-image.svg');
      background-size: auto, $noImageBackgroundSize;
      background-position: center 4px;
      background-repeat: no-repeat;

      @include hover {
        background-size: auto, calc(#{$noImageBackgroundSize} * 1.1);
      }
    }

    &__gradient {
      position: absolute;
      top: 0;
      width: 100%;
      height: 100%;

      background-image: $backgroundGradient;
      background-repeat: no-repeat;
      background-size: auto 100%;
      background-position: center;
    }

    &__link {
      position: absolute;
      top: 0;
      left: 0;
      height: 100%;
      width: 100%;
      z-index: 2;
    }

    &__title {
      @include smallText;

      position: absolute;
      bottom: 0;
      margin: 8px;

      transition: text-decoration-color 0.3s ease-in-out;
      text-decoration: underline;
      text-decoration-color: transparent;
      color: #fff;
      font-weight: 700;

      // lineclamp
      -webkit-box-orient: vertical;
      object-fit: contain;
      display: -webkit-box;
      -webkit-line-clamp: 5;
      line-clamp: 5;
      box-orient: vertical;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
</style>
